// certificateService.js - API service for certificate operations
import axios from 'axios';
import { loamsApi } from '@/services/api'; // Import your existing API service
// Import the logo as base64 to ensure it's embedded in the PDF
import phoenicianLogoSrc from '@/assets/ZZZZ.png';
// Import the top logo
import topLogoSrc from '@/assets/PhoenicianLogo.png';

// Create a specific axios instance for certificate operations
const certificateApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 30000, // 30 second timeout for PDF generation
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include authorization if needed
certificateApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);



// Error handler for better debugging
const handleApiError = (error) => {
  console.error('Certificate API Error:', error);
  
  if (error.response) {
    // The request was made and the server responded with an error status
    return Promise.reject(error.response.data || {
      error: `Server error: ${error.response.status}`
    });
  } else if (error.request) {
    // The request was made but no response was received
    return Promise.reject({
      error: 'No response received from server. Please check your connection.'
    });
  } else {
    // Something else caused the error
    return Promise.reject({
      error: error.message || 'Unknown error occurred'
    });
  }
};

// Certificate API service
const certificateService = {
  /**
   * Generate a single certificate PDF
   * @param {Object} data Certificate data
   * @returns {Promise<Object>} Response with certificate URL
   */


  getUserCertificates: async () => {
    try {
      console.log('[CERT] Fetching user certificates from localStorage');
      
      // Get certificates from localStorage (fallback)
      const stored = localStorage.getItem('downloaded-certificates');
      if (stored) {
        const certificates = JSON.parse(stored);
        console.log('[CERT] Found stored certificates:', certificates.length);
        return certificates;
      }
      
      // Alternative: Get recent certificates from API
      try {
  const response = await loamsApi.certificates.lists();
  if (response && response.certificates) {
    console.log('[CERT] Fetched certificates from API:', response.certificates.length);
    return response.certificates.slice(0, 10); // Return last 10
  }
} catch (apiError) {
  console.warn('[CERT] API fetch failed, using localStorage fallback:', apiError);
}
      
      return [];
    } catch (error) {
      console.error('[CERT] Error fetching user certificates:', error);
      return [];
    }
  },

  
  generateCertificate: async (data) => {
    try {
      // Create certificate object directly in the frontend
      const certificate = await createCertificateObject(data);
      
      // Return success with local URL and download info
      return {
        success: true,
        url: certificate.url,
        filename: certificate.filename,
        certificate: certificate.data
      };
    } catch (error) {
      console.error('Certificate generation error:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate certificate'
      };
    }
  },



   /**
   * Download certificate from S3
   * @param {string} key - S3 key
   * @param {string} filename - Download filename
   */
   downloadCertificate: async (key, filename) => {
    try {
      const previewData = await loamsApi.getPreviewUrl(key);
      
      if (previewData.success && previewData.url) {
        // Create download link
        const link = document.createElement('a');
        link.href = previewData.url;
        link.download = filename || 'certificate.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Record download with Dubai time
        const downloadRecord = {
          filename,
          key,
          downloadedAt: new Date().toLocaleString('en-AE', { timeZone: 'Asia/Dubai' }),
          downloadedBy: localStorage.getItem('userName') || 'Unknown',
        };
        
        const downloads = JSON.parse(localStorage.getItem('certificateDownloads') || '[]');
        downloads.push(downloadRecord);
        localStorage.setItem('certificateDownloads', JSON.stringify(downloads.slice(-100)));
        
        return true;
      }
      throw new Error('Failed to get download URL');
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  },


  /**
   * Get recently generated certificates
   */
  getRecentGenerations: () => {
    return JSON.parse(localStorage.getItem('generatedCertificates') || '[]');
  },

  /**
   * Get recent downloads
   */
  getRecentDownloads: () => {
    return JSON.parse(localStorage.getItem('certificateDownloads') || '[]');
  },


  
  
  /**
   * Download a certificate from URL
   * @param {string} url Certificate URL
   * @param {string} filename Filename for download
   */
  

  /**
   * Record certificate generation
   * @param {Object} data Generation data
   */
  recordGeneration: async (data) => {
    const key = `cert_generated_${data.cluster}_${data.agreementRef}_${data.month}`;
    const record = {
      ...data,
      generatedAt: new Date().toISOString(),
      generatedBy: localStorage.getItem('userName') || 'Unknown',
    };
    
    // Store in localStorage for tracking
    const existing = JSON.parse(localStorage.getItem('generatedCertificates') || '[]');
    existing.push(record);
    localStorage.setItem('generatedCertificates', JSON.stringify(existing.slice(-100))); // Keep last 100
    
    return record;
  },
  
  /**
   * Generate multiple certificates (batch processing)
   * @param {Object} data Batch parameters
   * @returns {Promise<Object>} Response with certificate URLs
   */
  generateBatchCertificates: async (data) => {
    try {
      // First validate batch parameters
      if (!data.month) {
        throw new Error('Month is required for batch generation');
      }
      
      // Get all valid agreements for this month
      const agreements = await fetchValidAgreements(data.month, data.serviceType, data.cluster);
      
      if (!agreements || agreements.length === 0) {
        throw new Error('No valid agreements found for the selected month');
      }
      
      // Generate certificates for each agreement
      const certificates = [];
      const blobFiles = [];
      
      for (const agreement of agreements) {
        try {
          // Generate certificate
          const certificate = await createCertificateObject({
            cluster: agreement.cluster,
            agreementRef: agreement.agreement_ref,
            month: data.month,
            serviceType: agreement.service_type
          });
          
          certificates.push({
            cluster: agreement.cluster,
            agreementRef: agreement.agreement_ref,
            url: certificate.url,
            filename: certificate.filename
          });
          
          // Convert blob URL to blob for ZIP
          if (data.downloadZip) {
            const response = await fetch(certificate.url);
            const blob = await response.blob();
            blobFiles.push({
              name: certificate.filename,
              blob: blob
            });
          }
        } catch (error) {
          console.error(`Error generating certificate for ${agreement.cluster}/${agreement.agreement_ref}:`, error);
          // Continue with other certificates
        }
      }
      
      // Create ZIP file if requested and JSZip is available
      if (data.downloadZip && blobFiles.length > 0) {
        try {
          // Import JSZip dynamically
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          
          // Add files to ZIP
          blobFiles.forEach(file => {
            zip.file(file.name, file.blob);
          });
          
          // Generate ZIP file
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const zipUrl = URL.createObjectURL(zipBlob);
          
          // Download ZIP file
          const link = document.createElement('a');
          link.href = zipUrl;
          link.download = `certificates_${data.month}.zip`;
          document.body.appendChild(link);
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(zipUrl);
          }, 100);
        } catch (error) {
          console.error('Error creating ZIP file:', error);
          throw new Error('Failed to create ZIP file: ' + error.message);
        }
      }
      
      return {
        success: true,
        certificates,
        count: certificates.length
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to generate certificates'
      };
    }
  }
};

// Helper function to fetch valid agreements for a month
async function fetchValidAgreements(month, serviceType, cluster) {
  try {
    // Call your API to get agreements for this month
    const response = await loamsApi.certificates.getMonthAgreements(month);
    
    if (!response.data || !response.data.agreements) {
      return [];
    }
    
    let agreements = response.data.agreements;
    
    // Filter by service type if provided
    if (serviceType && serviceType !== 'all') {
      agreements = agreements.filter(a => {
        const type = a.service_type ? a.service_type.toUpperCase() : '';
        if (serviceType === 'SWIMMING') {
          return type.includes('SWIMMING') || type.includes('POOL');
        } else if (serviceType === 'LANDSCAPING') {
          return type.includes('LANDSCAPING') || type.includes('LANDSCAPE') || 
                 type.includes('IRRIGATION');
        }
        return false;
      });
    }
    
    // Filter by cluster if provided
    if (cluster) {
      agreements = agreements.filter(a => a.cluster === cluster);
    }
    
    return agreements;
  } catch (error) {
    console.error('Error fetching valid agreements:', error);
    return [];
  }
}

/**
 * Helper function to record certificate downloads
 */
async function recordDownload(filename, userName) {
  try {
    // Extract information from filename (if needed)
    const parts = filename.split('_');
    const agreementRef = parts.length > 1 ? parts[1] : '';
    
    // Record download in localStorage for offline access
    const downloads = JSON.parse(localStorage.getItem('certificate-downloads') || '[]');
    downloads.push({
      filename,
      agreementRef,
      downloadedBy: userName,
      downloadTime: new Date().toISOString(),
      dubaiTime: new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' })
    });
    
    // Keep only the last 100 downloads
    if (downloads.length > 100) {
      downloads.splice(0, downloads.length - 100);
    }
    
    localStorage.setItem('certificate-downloads', JSON.stringify(downloads));
    
    // If API is available, also record on server
    if (loamsApi && loamsApi.certificates && loamsApi.certificates.recordDownload) {
      await loamsApi.certificates.recordDownload({
        filename,
        agreementRef,
        downloadedBy: userName
      });
    }
    
    return true;
  } catch (error) {
    console.warn('Error recording download:', error);
    return false;
  }
}

// Helper function to create a certificate object directly in the browser
async function createCertificateObject(data) {
  try {
    // Parse date from month (YYYY-MM format)
    const [year, monthNum] = data.month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
    const monthName = date.toLocaleString('default', { month: 'long' }).toUpperCase();
    const monthYear = `${monthName} ${year}`;
    
    // Determine if it's swimming pool or landscaping service
    const isSwimming = data.serviceType.toUpperCase().includes('SWIMMING') || 
                       data.serviceType.toUpperCase().includes('POOL');
    
    // Format service type properly
    const formattedServiceType = isSwimming 
      ? 'SWIMMING POOL CLEANING AND MAINTENANCE' 
      : 'LANDSCAPING AND IRRIGATION MAINTENANCE';
    
    // Preload the logo images to ensure they're included in the PDF
    const footerLogoImg = new Image();
    footerLogoImg.crossOrigin = 'Anonymous';
    
    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = 'Anonymous';
    
    // Create promises to wait for the images to load
    const loadFooterLogoPromise = new Promise((resolve) => {
      footerLogoImg.onload = () => {
        // Create a canvas to convert the image to base64
        const canvas = document.createElement('canvas');
        canvas.width = footerLogoImg.width;
        canvas.height = footerLogoImg.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(footerLogoImg, 0, 0);
        
        // Get base64 representation
        footerLogoImg.src = canvas.toDataURL('image/png');
        resolve();
      };
      footerLogoImg.onerror = (e) => {
        console.error('Footer logo failed to load', e);
        resolve(); // Continue even if image fails to load
      };
      footerLogoImg.src = phoenicianLogoSrc; // Use the imported logo src
    });



    
    
    const loadHeaderLogoPromise = new Promise((resolve) => {
      headerLogoImg.onload = () => {
        // Create a canvas to convert the image to base64
        const canvas = document.createElement('canvas');
        canvas.width = headerLogoImg.width;
        canvas.height = headerLogoImg.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(headerLogoImg, 0, 0);
        
        // Get base64 representation
        headerLogoImg.src = canvas.toDataURL('image/png');
        resolve();
      };
      headerLogoImg.onerror = (e) => {
        console.error('Header logo failed to load', e);
        resolve(); // Continue even if image fails to load
      };
      headerLogoImg.src = topLogoSrc; // Use the imported top logo src
    });
    
    // Wait for both logos to load before proceeding
    await Promise.all([loadFooterLogoPromise, loadHeaderLogoPromise]);
    
    // Generate PDF using client-side PDF library (using jspdf and html2canvas)
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;
    
    // Create a temporary div to hold certificate HTML
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '210mm'; // A4 width
    
    // Service description based on type
    const serviceDescription = isSwimming 
      ? `${data.cluster} Swimming Pools weekly seven days cleaning, vaccuming and chemical dosing works completed month of ${monthName}-${year}.`
      : `${data.cluster} Landscaping maintenance services work completed month of ${monthName}-${year}.`;
    
    // Create certificate HTML - updated with the top logo and footer
    // IMPORTANT: Separate the cluster name and service type into different paragraphs
    tempDiv.innerHTML = `
      <div class="certificate-content" style="padding: 20px; font-family: Arial, sans-serif; width: 210mm; min-height: 297mm;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
          <div style="text-align: center; flex: 1;">
            <p style="font-weight: bold;">TRN NO.: 100034783900003</p>
          </div>
          <div style="text-align: right;">
            <img id="header-logo" src="${headerLogoImg.src}" alt="Phoenician Logo" style="height: 60px; margin-bottom: 0px;">
          </div>
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
          <div>
            <p style="font-weight: bold;">TO,</p>
            <p style="font-weight: bold;">M/S LOAMS LLC,</p>
            <p style="font-weight: bold;">${data.cluster}</p>
            <p style="font-weight: bold;">${formattedServiceType}</p>
          </div>
          <div style="text-align: right;">
            <p style="font-weight: bold;">DATE: ${monthYear}</p>
          </div>
        </div>
        
        <h1 style="text-align: center; font-size: 18px; margin: 24px 0; font-weight: bold;">COMPLETION CERTIFICATE</h1>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="border: 1px solid black; padding: 8px; text-align: center; width: 30%; font-weight: bold;">
              ${isSwimming ? 'CC REF:' : 'PO REF:'}
            </td>
            <td style="border: 1px solid black; padding: 8px; text-align: center; width: 70%; font-weight: bold;">
              ${data.agreementRef}
            </td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 8px; text-align: center; font-weight: bold;">
              ${data.cluster}
            </td>
            <td style="border: 1px solid black; padding: 8px; text-align: center; font-weight: bold;">
              ${isSwimming ? 'SWIMMING POOLS CLEANING AND MAINTENANCE' : 'LANDSCAPING AND IRRIGATION MAINTENANCE'}
            </td>
          </tr>
        </table>
        
        <p style="margin-bottom: 15px; font-weight: bold;">We would like to inform you that the job awarded to us has been completed according to contract terms and schedule</p>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="border: 1px solid black; padding: 8px; width: 50%; text-align: center; font-weight: bold;">Description</td>
            <td style="border: 1px solid black; padding: 8px; width: 50%; text-align: center; font-weight: bold;">Completion Date</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 8px; height: 80px; vertical-align: top;">
              <p style="font-weight: bold;">${data.cluster}</p>
              <p>${serviceDescription}</p>
              <p style="margin-top: 10px; font-weight: bold;">Work Completed</p>
            </td>
            <td style="border: 1px solid black; padding: 8px; text-align: center; vertical-align: middle; font-weight: bold;">
              ${monthYear}
            </td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 8px; height: 110px; vertical-align: top; font-weight: bold;">
              Certified By: LOAMS LLC
            </td>
            <td style="border: 1px solid black; padding: 8px; vertical-align: top; font-weight: bold;">
              Name:<br>
              Signature:
            </td>
          </tr>
        </table>
        
        <p style="margin-bottom: 8px; font-weight: bold;">We certify that the job has been completed as per schedule and based on the scope of work mentioned there in</p>
        <p style="margin-bottom: 15px; font-weight: bold;">Should required any further information, please contact us on, 054-9918014</p>
        
        <div style="text-align: right; margin-top: 24px; font-weight: bold;">
          <p>For,</p>
          <p style="margin-top: 8px;">Phoenician Technical Services LLC</p>
        </div>

        <!-- Company footer section - Updated with larger font size -->
        <div style="margin-top: 30mm; padding-top: 10mm; border-top: 0.5px solid #eee; display: flex; justify-content: space-between; align-items: flex-end; width: 100%;">
          <!-- Company address - left side with increased font size -->
          <div style="font-size: 9px; color: #A2732E; text-align: left; font-weight: bold; line-height: 1.5;">
            PHOENICIAN TECHNICAL SERVICES LLC<br>
            OFFICE 399 CENTRAL PLAZA, DIP-1, DUBAI, UAE<br>
            P.O.BOX: 377872 - DUBAI<br>
            T: 048829484<br>
            W: WWW.PHOENICIAN-UAE.COM
          </div>
          
          <!-- Company logos - right side -->
          <div>
            <img id="footer-logo" src="${footerLogoImg.src}" alt="Company Logos" style="height: 15mm; object-fit: contain;">
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(tempDiv);
    
    // Convert to canvas with improved settings
    const canvas = await html2canvas(tempDiv, {
      scale: 2.5, // Increased scale for better quality
      useCORS: true,
      allowTaint: true, // Allow tainted canvas to include images
      logging: false, // Disable logging for production
      backgroundColor: '#FFFFFF',
      height: tempDiv.scrollHeight,
      windowHeight: tempDiv.scrollHeight
    });
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add canvas as image to PDF
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
    
    // Generate download URL
    const pdfBlob = pdf.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    
    // Remove temp div
    document.body.removeChild(tempDiv);
    
    // Create filename
    const refPrefix = isSwimming ? "CC" : "PO";
    const filename = `${refPrefix}_${data.agreementRef}_${monthName}_${year}.pdf`;
    
    return {
      url,
      filename,
      data: {
        cluster: data.cluster,
        agreementRef: data.agreementRef,
        month: monthYear,
        isSwimming,
        serviceType: data.serviceType,
        formattedServiceType: formattedServiceType,
        trn: '100034783900003'
      }
    };
  } catch (error) {
    console.error('Error creating certificate:', error);
    throw new Error('Failed to create certificate: ' + error.message);
  }
}

export default certificateService;